import { Dialog, Button } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import CustomFormatDatePicker from "../../../components/common/CustomFormatDatePicker";
import { useTheme } from "../../../components/hooks/useTheme";
import colorConfigs from "../../../configs/colorConfigs";
import moment from "moment";
import useConfirm from "../../../components/hooks/useConfirm";
import axios from "axios";
import baseURL from "../../../configs/api";
import { format } from "date-fns";
import { getCarsWithReservation, getTheAvailableCarsOFBranch } from "../../../services/availableCars";
import Cookies from "js-cookie";
import useTenant from "../../../components/hooks/useTenant";
import useGeneral from "../../../components/hooks/useGeneral";

type Props = {
  dateAndTimeBox: boolean;
  setDateAndTimeBox: (pickupBox: boolean) => void;
  pickupDate: string;
  pickupTime: string;
  returnDate: string;
  returnTime: string;
  getDataFromDate: (
    pickupDate: string,
    pickupTime: string,
    returnDate: string,
    returnTime: string,
    alertMessage: string,
  ) => void;
  reservationId: number;
  preferredDateFormat: string;
  locationOpenHourOfParticularBranch: any[];
  branches: any[];
  branchId: number;
  selectedVehicleClassId: number;
  setAvailableVehicle: (value: any[]) => void;
  setPleaseWait: (value: boolean) => void;
};

export default function DateAndTimeChange({
  dateAndTimeBox,
  setDateAndTimeBox,
  pickupDate,
  pickupTime,
  returnDate,
  returnTime,
  getDataFromDate,
  reservationId,
  preferredDateFormat,
  locationOpenHourOfParticularBranch,
  branches,
  branchId,
  selectedVehicleClassId,
  setAvailableVehicle,
  setPleaseWait
}: Props) {
  const tenantId = Cookies.get("tenantId-jj-rental-ui") || "";
  const { handleTenantError }: any = useTenant();
  const { dateFormat }: any = useGeneral();
  const { theme, themeStyles } = useTheme();
  const { setConfirmPopup, setData: setConfirmData }: any = useConfirm();
  const [pickupDateBox, setPickupDateBox] = useState<string>("");
  const [pickupTimeBox, setPickupTimeBox] = useState<string>("");
  const [returnDateBox, setReturnDateBox] = useState<string>("");
  const [returnTimeBox, setReturnTimeBox] = useState<string>("");
  const [isValid, setIsValid] = useState<boolean>(true);
  const [returnTimeMessage, setreturnTimeMessage] = useState<string>("");
  const [minBookingMessage, setMinBookingMessage] = useState<string>("");
  const closeIconFill = theme === "dark" ? "fill-[#E4E7EC]" : "fill-[#1D2939]";

  useEffect(() => {
    setPickupDateBox(pickupDate);
  }, [pickupDate]);

  useEffect(() => {
    setPickupTimeBox(pickupTime);
  }, [pickupTime]);

  useEffect(() => {
    setReturnDateBox(returnDate);
  }, [returnDate]);

  useEffect(() => {
    setReturnTimeBox(returnTime);
  }, [returnTime]);

  const selectedBranch = useMemo(() => {
    return branches?.find((branch) => branch?.id === branchId);
  }, [branchId, branches]);

  const minAllowedHoursToBook = selectedBranch ? selectedBranch?.minAllowedHoursToBook : 0;

  useEffect(() => {
    if (new Date(pickupDateBox)?.getTime() === new Date(returnDateBox)?.getTime() && returnTimeBox <= pickupTimeBox) {
      setIsValid(false);
      setreturnTimeMessage("Return Time must greater than pickup Time");
    } else {
      const pickupDateTime = new Date(`${pickupDateBox}T${pickupTimeBox}`);
      const returnDateTime = new Date(`${returnDateBox}T${returnTimeBox}`);

      const timeDifferenceInMs = returnDateTime.getTime() - pickupDateTime.getTime();
      const timeDifferenceInHours = timeDifferenceInMs / (1000 * 60 * 60);

      if (new Date(pickupDateBox).getTime() === new Date(returnDateBox).getTime()) {
        if (timeDifferenceInHours < minAllowedHoursToBook) {
          setMinBookingMessage(
            `The minimum booking duration is ${minAllowedHoursToBook} hours.`
          );
          setIsValid(false);
          setreturnTimeMessage("");
        } else {
          setMinBookingMessage("");
          setIsValid(true);
          setreturnTimeMessage("");
        }
      } else {
        setMinBookingMessage("");
        setIsValid(true);
        setreturnTimeMessage("");
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickupDateBox, pickupTimeBox, returnDateBox, returnTimeBox]);

  const handleSave = () => {
    if (isValid) {
      if (
        new Date(locationOpenHourOfParticularBranch[0]?.startDate) <=
        new Date(pickupDateBox.replaceAll("-", "/")) &&
        new Date(locationOpenHourOfParticularBranch[0]?.endDate) >=
        new Date(returnDateBox.replaceAll("-", "/"))
      ) {
        const pickupDateTime = moment(`${pickupDateBox}T${pickupTimeBox}`).format('YYYY-MM-DDTHH:mm');
        const returnDateTime = moment(`${returnDateBox}T${returnTimeBox}`).format('YYYY-MM-DDTHH:mm');
        axios.get(`${baseURL}blockedDay/GetBlockedDaysByStartDateAndEndDate/${pickupDateTime},${returnDateTime}`, {
          headers: {
            'X-Tenant-ID': tenantId,
            "Content-Type": "application/json"
          }
        })
          .then((res) => {
            if (res.data.length > 0) {
              if (res.data[0]?.isAllDay) {
                setConfirmData({
                  title: `We are closed on ${format(new Date(res.data[0]?.startDate.split('T')[0]), preferredDateFormat.replace('DD', 'dd')) === format(new Date(pickupDateBox), preferredDateFormat.replace('DD', 'dd')) ?
                    format(new Date(pickupDateBox), preferredDateFormat.replace('DD', 'dd'))
                    :
                    format(new Date(res.data[0]?.startDate.split('T')[0]), preferredDateFormat.replace('DD', 'dd')) === format(new Date(returnDateBox), preferredDateFormat.replace('DD', 'dd')) ?
                      format(new Date(returnDateBox), preferredDateFormat.replace('DD', 'dd'))
                      :
                      format(new Date(pickupDateBox), preferredDateFormat.replace('DD', 'dd'))
                    }`,
                  summary: 'Are you sure you want to proceed?',
                  isConfirm: (callback: any) => {
                    const alerMessage = `We are closed on ${format(new Date(res.data[0]?.startDate.split('T')[0]), preferredDateFormat.replace('DD', 'dd')) === format(new Date(pickupDateBox), preferredDateFormat.replace('DD', 'dd')) ?
                      format(new Date(pickupDateBox), preferredDateFormat.replace('DD', 'dd'))
                      :
                      format(new Date(res.data[0]?.startDate.split('T')[0]), preferredDateFormat.replace('DD', 'dd')) === format(new Date(returnDateBox), preferredDateFormat.replace('DD', 'dd')) ?
                        format(new Date(returnDateBox), preferredDateFormat.replace('DD', 'dd'))
                        :
                        format(new Date(pickupDateBox), preferredDateFormat.replace('DD', 'dd'))
                      }`

                    changeTheDate(alerMessage, callback);
                  }
                });
                setConfirmPopup(true);
              } else {
                setConfirmData({
                  title: `We are closed on ${format(new Date(res.data[0]?.startDate.split('T')[0]), preferredDateFormat.replace('DD', 'dd')) === format(new Date(pickupDateBox), preferredDateFormat.replace('DD', 'dd')) ?
                    format(new Date(pickupDateBox), preferredDateFormat.replace('DD', 'dd'))
                    :
                    format(new Date(res.data[0]?.startDate.split('T')[0]), preferredDateFormat.replace('DD', 'dd')) === format(new Date(returnDateBox), preferredDateFormat.replace('DD', 'dd')) ?
                      format(new Date(returnDateBox), preferredDateFormat.replace('DD', 'dd'))
                      :
                      ""
                    } between ${format(new Date(res.data[0]?.startDate.split('T')[0] + "T" + res.data[0]?.startTime), "hh:mm aaaaa'm'")} to ${format(new Date(res.data[0]?.startDate.split('T')[0] + "T" + res.data[0]?.endTIme), "hh:mm aaaaa'm'")}`,
                  summary: 'Are you sure you want to proceed?',
                  isConfirm: (callback: any) => {
                    const alerMessage = `We are closed on ${format(new Date(res.data[0]?.startDate.split('T')[0]), preferredDateFormat.replace('DD', 'dd')) === format(new Date(pickupDateBox), preferredDateFormat.replace('DD', 'dd')) ?
                      format(new Date(pickupDateBox), preferredDateFormat.replace('DD', 'dd'))
                      :
                      format(new Date(res.data[0]?.startDate.split('T')[0]), preferredDateFormat.replace('DD', 'dd')) === format(new Date(returnDateBox), preferredDateFormat.replace('DD', 'dd')) ?
                        format(new Date(returnDateBox), preferredDateFormat.replace('DD', 'dd'))
                        :
                        ""
                      } between ${format(new Date(res.data[0]?.startDate.split('T')[0] + "T" + res.data[0]?.startTime), "hh:mm aaaaa'm'")} to ${format(new Date(res.data[0]?.startDate.split('T')[0] + "T" + res.data[0]?.endTIme), "hh:mm aaaaa'm'")}`;

                    changeTheDate(alerMessage, callback);
                  }

                });
                setConfirmPopup(true);
              }
            } else {
              let pickupDateMessages = "";
              let returnDateMessages = "";
              let pickupTimeMessages = "";
              let returnTimeMessages = "";
              switch (new Date(pickupDateBox.replaceAll("-", "/")).getDay()) {
                case 0:
                  if (locationOpenHourOfParticularBranch[0].isSundayOpen) {
                    pickupDateMessages = "";
                    if (
                      locationOpenHourOfParticularBranch[0].sundayOpenTime <=
                      pickupTimeBox &&
                      locationOpenHourOfParticularBranch[0].sundayCloseTime >=
                      pickupTimeBox
                    ) {
                      pickupTimeMessages = "";
                    } else {
                      pickupTimeMessages =
                        "Office on Sunday is open from " +
                        moment(locationOpenHourOfParticularBranch[0].sundayOpenTime, "HH:mm").format("hh:mm a") +
                        " to " +
                        moment(locationOpenHourOfParticularBranch[0].sundayCloseTime, "HH:mm").format("hh:mm a");
                    }
                  } else {
                    pickupDateMessages = "Office is closed on Sunday";
                  }
                  break;
                case 1:
                  if (locationOpenHourOfParticularBranch[0].isMondayOpen) {
                    pickupDateMessages = "";
                    if (
                      locationOpenHourOfParticularBranch[0].mondayOpenTime <=
                      pickupTimeBox &&
                      locationOpenHourOfParticularBranch[0].mondayCloseTime >=
                      pickupTimeBox
                    ) {
                      pickupTimeMessages = "";
                    } else {
                      pickupTimeMessages = "Office on Monday is open from " +
                        moment(locationOpenHourOfParticularBranch[0].mondayOpenTime, "HH:mm").format("hh:mm a") +
                        " to " +
                        moment(locationOpenHourOfParticularBranch[0].mondayCloseTime, "HH:mm").format("hh:mm a");
                    }
                  } else {
                    pickupDateMessages = "Office is closed on Monday";
                  }
                  break;
                case 2:
                  if (locationOpenHourOfParticularBranch[0].isTuesdayOpen) {
                    pickupDateMessages = "";
                    if (
                      locationOpenHourOfParticularBranch[0].tuesdayOpenTime <=
                      pickupTimeBox &&
                      locationOpenHourOfParticularBranch[0].tuesdayCloseTime >=
                      pickupTimeBox
                    ) {
                      pickupTimeMessages = "";
                    } else {
                      pickupTimeMessages =
                        "Office on Tuesday is open from " +
                        moment(locationOpenHourOfParticularBranch[0].tuesdayOpenTime, "HH:mm").format("hh:mm a") +
                        " to " +
                        moment(locationOpenHourOfParticularBranch[0].tuesdayCloseTime, "HH:mm").format("hh:mm a");
                    }
                  } else {
                    pickupDateMessages = "Office is closed on Tuesday";
                  }
                  break;
                case 3:
                  if (locationOpenHourOfParticularBranch[0].isWednesdayOpen) {
                    pickupDateMessages = "";
                    if (
                      locationOpenHourOfParticularBranch[0].wednesdayOpenTime <=
                      pickupTimeBox &&
                      locationOpenHourOfParticularBranch[0].wednesdayCloseTime >=
                      pickupTimeBox
                    ) {
                      pickupTimeMessages = "";
                    } else {
                      pickupTimeMessages =
                        "Office on Wednesday is open from " +
                        moment(locationOpenHourOfParticularBranch[0].wednesdayOpenTime, "HH:mm").format("hh:mm a") +
                        " to " +
                        moment(locationOpenHourOfParticularBranch[0].wednesdayCloseTime, "HH:mm").format("hh:mm a");
                    }
                  } else {
                    pickupDateMessages = "Office is closed on Wednesday";
                  }
                  break;
                case 4:
                  if (locationOpenHourOfParticularBranch[0].isThursdayOpen) {
                    pickupDateMessages = "";
                    if (
                      locationOpenHourOfParticularBranch[0].thursdayOpenTime <=
                      pickupTimeBox &&
                      locationOpenHourOfParticularBranch[0].thursdayCloseTime >=
                      pickupTimeBox
                    ) {
                      pickupTimeMessages = "";
                    } else {
                      pickupTimeMessages =
                        "Office on Thursday is open from " +
                        moment(locationOpenHourOfParticularBranch[0].thursdayOpenTime, "HH:mm").format("hh:mm a") +
                        " to " +
                        moment(locationOpenHourOfParticularBranch[0].thursdayCloseTime, "HH:mm").format("hh:mm a");
                    }
                  } else {
                    pickupDateMessages = "Office is closed on Thursday";
                  }
                  break;
                case 5:
                  if (locationOpenHourOfParticularBranch[0].isFridayOpen) {
                    pickupDateMessages = "";
                    if (
                      locationOpenHourOfParticularBranch[0].fridayOpenTime <=
                      pickupTimeBox &&
                      locationOpenHourOfParticularBranch[0].fridayCloseTime >=
                      pickupTimeBox
                    ) {
                      pickupTimeMessages = "";
                    } else {
                      pickupTimeMessages =
                        "Office on Friday is open from " +
                        moment(locationOpenHourOfParticularBranch[0].fridayOpenTime, "HH:mm").format("hh:mm a") +
                        " to " +
                        moment(locationOpenHourOfParticularBranch[0].fridayCloseTime, "HH:mm").format("hh:mm a");
                    }
                  } else {
                    pickupDateMessages = "Office is closed on Friday";
                  }
                  break;
                case 6:
                  if (locationOpenHourOfParticularBranch[0].isSaturdayOpen) {
                    pickupDateMessages = "";
                    if (
                      locationOpenHourOfParticularBranch[0].saturdayOpenTime <=
                      pickupTimeBox &&
                      locationOpenHourOfParticularBranch[0].saturdayCloseTime >=
                      pickupTimeBox
                    ) {
                      pickupTimeMessages = "";
                    } else {
                      pickupTimeMessages =
                        "Office on Saturday is open from " +
                        moment(locationOpenHourOfParticularBranch[0].saturdayOpenTime, "HH:mm").format("hh:mm a") +
                        " to " +
                        moment(locationOpenHourOfParticularBranch[0].saturdayCloseTime, "HH:mm").format("hh:mm a");
                    }
                  } else {
                    pickupDateMessages = "Office is closed on Saturday";
                  }
                  break;
              }

              switch (new Date(returnDateBox.replaceAll("-", "/")).getDay()) {
                case 0:
                  if (locationOpenHourOfParticularBranch[0].isSundayOpen) {
                    returnDateMessages = "";
                    if (
                      locationOpenHourOfParticularBranch[0].sundayOpenTime <=
                      returnTimeBox &&
                      locationOpenHourOfParticularBranch[0].sundayCloseTime >=
                      returnTimeBox
                    ) {
                      returnTimeMessages = "";
                    } else {
                      returnTimeMessages =
                        "Office on Sunday is open from " +
                        moment(locationOpenHourOfParticularBranch[0].sundayOpenTime, "HH:mm").format("hh:mm a") +
                        " to " +
                        moment(locationOpenHourOfParticularBranch[0].sundayCloseTime, "HH:mm").format("hh:mm a");
                    }
                  } else {
                    returnDateMessages = "Office is closed on Sunday";
                  }
                  break;
                case 1:
                  if (locationOpenHourOfParticularBranch[0].isMondayOpen) {
                    returnDateMessages = "";
                    if (
                      locationOpenHourOfParticularBranch[0].mondayOpenTime <=
                      returnTimeBox &&
                      locationOpenHourOfParticularBranch[0].mondayCloseTime >=
                      returnTimeBox
                    ) {
                      returnTimeMessages = "";
                    } else {
                      returnTimeMessages =
                        "Office on Monday is open from " +
                        moment(locationOpenHourOfParticularBranch[0].mondayOpenTime, "HH:mm").format("hh:mm a") +
                        " to " +
                        moment(locationOpenHourOfParticularBranch[0].mondayCloseTime, "HH:mm").format("hh:mm a");
                    }
                  } else {
                    returnDateMessages = "Office is closed on Monday";
                  }
                  break;
                case 2:
                  if (locationOpenHourOfParticularBranch[0].isTuesdayOpen) {
                    returnDateMessages = "";
                    if (
                      locationOpenHourOfParticularBranch[0].tuesdayOpenTime <=
                      returnTimeBox &&
                      locationOpenHourOfParticularBranch[0].tuesdayCloseTime >=
                      returnTimeBox
                    ) {
                      returnTimeMessages = "";
                    } else {
                      returnTimeMessages =
                        "Office on Tuesday is open from " +
                        moment(locationOpenHourOfParticularBranch[0].tuesdayOpenTime, "HH:mm").format("hh:mm a") +
                        " to " +
                        moment(locationOpenHourOfParticularBranch[0].tuesdayCloseTime, "HH:mm").format("hh:mm a");
                    }
                  } else {
                    returnDateMessages = "Office is closed on Tuesday";
                  }
                  break;
                case 3:
                  if (locationOpenHourOfParticularBranch[0].isWednesdayOpen) {
                    returnDateMessages = "";
                    if (
                      locationOpenHourOfParticularBranch[0].wednesdayOpenTime <=
                      returnTimeBox &&
                      locationOpenHourOfParticularBranch[0].wednesdayCloseTime >=
                      returnTimeBox
                    ) {
                      returnTimeMessages = "";
                    } else {
                      returnTimeMessages =
                        "Office on Wednesday is open from " +
                        moment(locationOpenHourOfParticularBranch[0].wednesdayOpenTime, "HH:mm").format("hh:mm a") +
                        " to " +
                        moment(locationOpenHourOfParticularBranch[0].wednesdayCloseTime, "HH:mm").format("hh:mm a");
                    }
                  } else {
                    returnDateMessages = "Office is closed on Wednesday";
                  }
                  break;
                case 4:
                  if (locationOpenHourOfParticularBranch[0].isThursdayOpen) {
                    returnDateMessages = "";
                    if (
                      locationOpenHourOfParticularBranch[0].thursdayOpenTime <=
                      returnTimeBox &&
                      locationOpenHourOfParticularBranch[0].thursdayCloseTime >=
                      returnTimeBox
                    ) {
                      returnTimeMessages = "";
                    } else {
                      returnTimeMessages =
                        "Office on Thursday is open from " +
                        moment(locationOpenHourOfParticularBranch[0].thursdayOpenTime, "HH:mm").format("hh:mm a") +
                        " to " +
                        moment(locationOpenHourOfParticularBranch[0].thursdayCloseTime, "HH:mm").format("hh:mm a");
                    }
                  } else {
                    returnDateMessages = "Office is closed on Thursday";
                  }
                  break;
                case 5:
                  if (locationOpenHourOfParticularBranch[0].isFridayOpen) {
                    returnDateMessages = "";
                    if (
                      locationOpenHourOfParticularBranch[0].fridayOpenTime <=
                      returnTimeBox &&
                      locationOpenHourOfParticularBranch[0].fridayCloseTime >=
                      returnTimeBox
                    ) {
                      returnTimeMessages = "";
                    } else {
                      returnTimeMessages =
                        "Office on Friday is open from " +
                        moment(locationOpenHourOfParticularBranch[0].fridayOpenTime, "HH:mm").format("hh:mm a") +
                        " to " +
                        moment(locationOpenHourOfParticularBranch[0].fridayCloseTime, "HH:mm").format("hh:mm a");
                    }
                  } else {
                    returnDateMessages = "Office is closed on Friday";
                  }
                  break;
                case 6:
                  if (locationOpenHourOfParticularBranch[0].isSaturdayOpen) {
                    returnDateMessages = "";
                    if (
                      locationOpenHourOfParticularBranch[0].saturdayOpenTime <=
                      returnTimeBox &&
                      locationOpenHourOfParticularBranch[0].saturdayCloseTime >=
                      returnTimeBox
                    ) {
                      returnTimeMessages = "";
                    } else {
                      returnTimeMessages =
                        "Office on Saturday is open from " +
                        moment(locationOpenHourOfParticularBranch[0].saturdayOpenTime, "HH:mm").format("hh:mm a") +
                        " to " +
                        moment(locationOpenHourOfParticularBranch[0].saturdayCloseTime, "HH:mm").format("hh:mm a");
                    }
                  } else {
                    returnDateMessages = "Office is closed on Saturday";
                  }
                  break;
              }

              if (
                pickupDateMessages === ""
                && pickupTimeMessages === ""
                && returnDateMessages === ""
                && returnTimeMessages === ""
                && minBookingMessage === ""
              ) {
                const alerMessage = "";
                changeTheDate(alerMessage);
              } else {
                setConfirmPopup(true);
                setConfirmData({
                  title: `${pickupDateMessages} ${pickupTimeMessages} ${returnDateMessages} ${returnTimeMessages}`,
                  summary: 'Are you sure you want to proceed?',
                  isConfirm: (callback: any) => {
                    const alerMessage = `${pickupDateMessages} ${pickupTimeMessages} ${returnDateMessages} ${returnTimeMessages}`;
                    changeTheDate(alerMessage, callback);
                  }
                });
              }
            }
            handleTenantError(null);
          })
          .catch((error) => {
            handleTenantError(error);
          })
      } else {
        setConfirmPopup(true);
        setConfirmData({
          title: `We are closed on ${format(new Date(pickupDateBox), preferredDateFormat.replace('DD', 'dd'))} and ${format(new Date(returnDateBox), preferredDateFormat.replace('DD', 'dd'))}`,
          summary: 'Are you sure you want to proceed?',
          isConfirm: (callback: any) => {
            const alerMessage = `We are closed on ${format(new Date(pickupDateBox), preferredDateFormat.replace('DD', 'dd'))} and ${format(new Date(returnDateBox), preferredDateFormat.replace('DD', 'dd'))}`;
            changeTheDate(alerMessage, callback);
          }
        });
      }
    }
  };

  const changeTheDate = (alerMessage: string, callback?: any) => {
    setPleaseWait(true);

    if (selectedVehicleClassId > 0) {
      let latestPickup = moment().format('YYYY-MM-DDTHH:mm');
      let latestReturn = moment().format('YYYY-MM-DDTHH:mm');
      if (pickupDateBox) latestPickup = moment(`${pickupDateBox}T${pickupTimeBox}`).format('YYYY-MM-DDTHH:mm');
      if (returnDateBox) latestReturn = moment(`${returnDateBox}T${returnTimeBox}`).format('YYYY-MM-DDTHH:mm');

      if (reservationId > 0) {
        getCarsWithReservation(reservationId, latestPickup, latestReturn, branchId, tenantId)
          .then((data) => {
            const selectedVehicles = data?.find((vehicleClass: any) => vehicleClass?.vehicleClassId === selectedVehicleClassId);

            if (selectedVehicles?.availableVehicles > 0) {
              setAvailableVehicle(data);
              getDataFromDate(
                pickupDateBox,
                pickupTimeBox,
                returnDateBox,
                returnTimeBox,
                alerMessage
              );

              setDateAndTimeBox(false);
              callback && callback();
            } else {
              setConfirmData({
                title: `Between ${moment(latestPickup)?.format(dateFormat + " hh:mm A")} - ${moment(latestReturn)?.format(dateFormat + " hh:mm A")} dates selected vehicle is not available`,
                summary: '',
                confirmBtn: 'Ok',
                isConfirm: (callback: any) => {
                  callback();
                }
              });
              setConfirmPopup(true);
              setPleaseWait(false);
            }
            handleTenantError(null);
          })
          .catch((error) => {
            setPleaseWait(false);
            setConfirmData({
              title: 'Warning',
              summary: error.response.data,
              confirmBtn: 'Ok',
              isConfirm: (callback: any) => {
                callback();
              }
            });

            setConfirmPopup(true);
            handleTenantError(error);
          });
      } else {
        getTheAvailableCarsOFBranch(latestPickup, latestReturn, branchId, tenantId)
          .then((data) => {
            const selectedVehicles = data?.find((vehicleClass: any) => vehicleClass?.vehicleClassId === selectedVehicleClassId);

            if (selectedVehicles?.availableVehicles > 0) {
              setAvailableVehicle(data);
              getDataFromDate(
                pickupDateBox,
                pickupTimeBox,
                returnDateBox,
                returnTimeBox,
                alerMessage
              );
              setDateAndTimeBox(false);
              callback && callback();
            } else {
              setConfirmData({
                title: `Between ${moment(latestPickup)?.format(dateFormat + " hh:mm A")} - ${moment(latestReturn)?.format(dateFormat + " hh:mm A")} dates selected vehicle is not available`,
                summary: '',
                confirmBtn: 'Ok',
                isConfirm: (callback: any) => {
                  callback();
                }
              });
              setConfirmPopup(true);
              setPleaseWait(false);
            }
            handleTenantError(null);
          })
          .catch((error) => {
            setPleaseWait(false);
            setConfirmData({
              title: 'Warning',
              summary: error.response.data,
              confirmBtn: 'Ok',
              isConfirm: (callback: any) => {
                callback();
              }
            });

            setConfirmPopup(true);
            handleTenantError(error);
          });
      }
    } else {
      getDataFromDate(
        pickupDateBox,
        pickupTimeBox,
        returnDateBox,
        returnTimeBox,
        alerMessage
      );

      setDateAndTimeBox(false);
      callback && callback();
    }
  }

  return (
    <>
      <Dialog
        open={dateAndTimeBox}
        sx={{
          "& .MuiDialog-container": {
            alignItems: "self-start",
          },
          '& .MuiDialog-paper': {
            maxWidth: '450px',
            width: '100%',
            borderRadius: '16px',
            overflowY: "unset",
          },
        }}
      >

        <div className={`px-[15px] py-[15px] h-full ${themeStyles.main.tailwindSectionBg}`}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 320.591 320.591"
            className={`w-3 cursor-pointer float-right ${closeIconFill}`}
            onClick={() => setDateAndTimeBox(false)}
          >
            <path d="M30.391 318.583a30.37 30.37 0 0 1-21.56-7.288c-11.774-11.844-11.774-30.973 0-42.817L266.643 10.665c12.246-11.459 31.462-10.822 42.921 1.424 10.362 11.074 10.966 28.095 1.414 39.875L51.647 311.295a30.366 30.366 0 0 1-21.256 7.288z" />
            <path d="M287.9 318.583a30.37 30.37 0 0 1-21.257-8.806L8.83 51.963C-2.078 39.225-.595 20.055 12.143 9.146c11.369-9.736 28.136-9.736 39.504 0l259.331 257.813c12.243 11.462 12.876 30.679 1.414 42.922-.456.487-.927.958-1.414 1.414a30.368 30.368 0 0 1-23.078 7.288z" />
          </svg>
          <div className={`w-full bg-transparent rounded-lg p-[10px] flex flex-col gap-2.51`}>
            <div className="w-full mt-2">
              <div className={`w-full`}>
                <div className={`flex justify-between items-center p-2 border-b ${themeStyles.main.borderColor}`}>
                  <div className="flex items-center gap-1.5">
                    <h2 className={`text-lg font-semibold ${themeStyles.main.textHeadingColor}`}>Update Date and Times</h2>
                  </div>
                </div>

                <div className="mt-2 p-2">
                  <div className="w-full">
                    <div className="grid grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <div>
                          <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Pickup Date</label>
                          <CustomFormatDatePicker
                            value={pickupDateBox}
                            onChange={(value) => {
                              setPickupDateBox(value)
                              if (new Date(value) >= new Date(returnDateBox)) {
                                setReturnDateBox(moment(value).add(1, 'day').format('YYYY-MM-DD'))
                              }
                            }}
                            dateFormat={preferredDateFormat}
                            required={true}
                          />
                        </div>

                        <div>
                          <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Return Date</label>
                          <CustomFormatDatePicker
                            value={returnDateBox}
                            onChange={setReturnDateBox}
                            dateFormat={preferredDateFormat}
                            minDate={pickupDateBox}
                            required={true}
                          />
                        </div>
                      </div>
                      <div className="space-y-4">
                        <div>
                          <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Pickup Time</label>
                          <input
                            type="time"
                            className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                            value={pickupTimeBox}
                            onChange={(e) => setPickupTimeBox(e.target.value)}
                          />
                        </div>

                        <div>
                          <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Return Time</label>
                          <input
                            type="time"
                            className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                            value={returnTimeBox}
                            onChange={(e) => setReturnTimeBox(e.target.value)}
                          />
                          {
                            returnTimeMessage && (
                              <ul className="text-xs text-red-500 m-1">
                                <li>{returnTimeMessage}</li>
                              </ul>
                            )
                          }

                          {
                            minBookingMessage && (
                              <ul className="text-xs text-red-500 m-1">
                                <li>{minBookingMessage}</li>
                              </ul>
                            )
                          }
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-4 mt-6">
                      <Button variant="contained"
                        sx={{
                          backgroundColor: colorConfigs.btn.main.bg
                        }}
                        onClick={handleSave}
                      >
                        Save
                      </Button>

                      <Button variant="contained"
                        sx={{
                          backgroundColor: colorConfigs.btn.cancel.bg,
                          border: colorConfigs.btn.cancel.border,
                          color: colorConfigs.btn.cancel.color
                        }}
                        onClick={() => setDateAndTimeBox(false)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Dialog>
    </>
  );
}
